import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { CornerDownLeft } from 'lucide-react';
import { ForwardedRef, Fragment, forwardRef } from 'react';

import { usePlatform } from '@/hooks/usePlatform';

export type KbdProps = React.HTMLAttributes<HTMLElement> & {
    asChild?: boolean;
};

const MAC_MODIFIERS: Record<string, string> = {
    ctrl: '⌃',
    alt: '⌥',
    shift: '⇧',
    cmd: '⌘'
};

const NON_MAC_MODIFIERS: Record<string, string> = {
    ctrl: 'Ctrl',
    cmd: 'Ctrl',
    alt: 'Alt',
    shift: 'Shift'
};

const renderKey = (key: string): React.ReactNode => {
    if (key === 'enter') return <CornerDownLeft className="size-4!" />;
    if (key.length === 1) return key.toUpperCase();
    return key.charAt(0).toUpperCase() + key.slice(1);
};

const Kbd = forwardRef(
    (
        { asChild, children, className, ...kbdProps }: KbdProps,
        forwardedRef: ForwardedRef<HTMLElement>
    ) => {
        const { isMac } = usePlatform();
        const Comp = asChild ? Slot : 'kbd';

        const formatChildren = (child: React.ReactNode): React.ReactNode => {
            if (typeof child !== 'string') return child;

            const tokens = child
                .toLowerCase()
                .split('+')
                .map((token) => token.trim())
                .filter(Boolean);
            const glyphs = isMac ? MAC_MODIFIERS : NON_MAC_MODIFIERS;
            const modifiers = Object.keys(glyphs).filter((modifier) =>
                tokens.includes(modifier)
            );
            const keys = tokens.filter((token) => !(token in glyphs));

            const parts = [
                ...modifiers.map((modifier) => glyphs[modifier]),
                ...keys.map(renderKey)
            ];

            return parts.map((part, index) => (
                <Fragment key={index}>
                    {!isMac && index > 0 ? '+' : null}
                    {part}
                </Fragment>
            ));
        };

        const formattedChildren = Array.isArray(children)
            ? children.map(formatChildren)
            : formatChildren(children);

        return (
            <Comp
                {...kbdProps}
                className={cn(
                    'inline-flex select-none items-center justify-center whitespace-nowrap rounded-[4px] bg-muted px-1 py-px font-mono text-sm tracking-tight text-muted-foreground shadow-sm',
                    className
                )}
                ref={forwardedRef}
            >
                {formattedChildren}
            </Comp>
        );
    }
);
Kbd.displayName = 'Kbd';

export { Kbd };
